import { isEmbeddedTrips, notifyTripsParent, publicTripsUrl } from "../../utils/embeddedTrips";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ForestOutlinedIcon from "@mui/icons-material/ForestOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PropaneTankOutlinedIcon from "@mui/icons-material/PropaneTankOutlined";
import PublicIcon from "@mui/icons-material/Public";
import WaterOutlinedIcon from "@mui/icons-material/WaterOutlined";
import Popover from "@mui/material/Popover";
import PopupState, { bindPopover, bindTrigger } from "material-ui-popup-state";
import queryString from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../../GlobalStates";
import useQueryParams from "../../customHooks/useQueryParams";
import { useWindowSize } from "../../customHooks/useWindowSize";
import { isNumber, translateMonth } from "../../utils/utils";
import { Filter } from "./Filter/Filter";
import { FilterMobile } from "./Filter/FilterMobile";
import { MenuButton } from "./MenuButton";
import { motion, AnimatePresence } from "framer-motion";
import { ConciergeChatPanel } from "./ConciergeChatPanel";
import { Globe } from "lucide-react";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { useTranslation } from "react-i18next";
import "./header.scss";
import "./header.scss";
import { useIsMobile } from "../../hooks/responsive";
import { dates } from "../../constants/dates";
import authAxios from "../../authAxios";
import { useIsAuthenticated } from "../../utils/auth";
import { Button as KitButton, Dialog as KitDialog, Segmented as KitSegmented } from "../../pages/manage/ui";
import { defaultBroadCategories } from "../../utils/constants";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { communityToCurrencyMap } from "../../utils/multi-language";
import { countryWithLandingPage } from "../../utils/constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ImageResizable } from "../elements/ImageResizeable";
import { AllTripsFilterSheet } from "./Filter/AllTripsFilterSheet";
import {
  serializeAdvancedFilters,
  parseAdvancedFilters,
  ADVANCED_FILTER_QUERY_KEYS,
} from "../../home/filterQuery";
import { SECTION_TITLE_KEYS } from "../../services/trip";
import { setAdminNearbyLocationForCommunity } from "../../services/featured";
import { isEventsListView } from "../../utils/eventsList";
import { adventureStyleLabelMap, broadCategoryOptions } from "../../constants/adventureStyles";
import { formatCurrency } from "../../utils/currency";

const compactPrice = (v) => {
  if (v == null) return "∞";
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return String(v);
};

/** Short currency symbol for pill display (e.g. "$", "RM", "Rp") */
const getShortCurrencySymbol = () => {
  const currency = (localStorage.getItem("currency") || "SGD").toUpperCase();
  const map = {
    USD: "$",
    SGD: "$",
    HKD: "$",
    CAD: "$",
    NZD: "$",
    AUD: "$",
    IDR: "Rp",
    KRW: "₩",
    JPY: "¥",
    EUR: "€",
    GBP: "£",
    MYR: "RM",
    PHP: "₱",
    THB: "฿",
    VND: "₫",
    CNY: "¥",
    TWD: "NT$",
  };
  return map[currency] || "$";
};

const FilterPillsRow = ({ locationSearch, onOpenFilters, facetBounds }) => {
  const filters = parseAdvancedFilters(new URLSearchParams(locationSearch));
  const {
    durationMin,
    durationMax,
    priceMin,
    priceMax,
    adventureStyles,
    broadCategories,
    weekend,
  } = filters;

  const hasDuration = durationMin != null || durationMax != null;
  const hasPrice = priceMin != null || priceMax != null;
  const hasAdventure = adventureStyles.length > 0 || broadCategories.length > 0 || weekend;

  // Smart duration label
  const durationLabel = (() => {
    if (!hasDuration) return "Duration";
    const dMin = durationMin;
    const dMax = durationMax;
    if (dMin != null && dMax != null && dMin === dMax && dMin === 1) return "Day trip";
    if (dMin != null && dMax != null && dMin === dMax) return `${dMin}D`;
    const isAtMin =
      dMin == null || (facetBounds?.durationMin != null && dMin <= facetBounds.durationMin);
    const isAtMax =
      dMax == null || (facetBounds?.durationMax != null && dMax >= facetBounds.durationMax);
    if (isAtMin && !isAtMax) return `Under ${dMax} days`;
    if (!isAtMin && isAtMax) return `Above ${dMin} days`;
    return `${dMin ?? "1"}D – ${dMax ?? "∞"}D`;
  })();

  // Smart price label with short currency
  const sym = getShortCurrencySymbol();
  const priceLabel = (() => {
    if (!hasPrice) return "Price";
    const pMin = priceMin;
    const pMax = priceMax;
    const isAtMin =
      pMin == null || pMin === 0 || (facetBounds?.priceMin != null && pMin <= facetBounds.priceMin);
    const isAtMax = pMax == null || (facetBounds?.priceMax != null && pMax >= facetBounds.priceMax);
    if (isAtMin && !isAtMax) return `Under ${sym}${compactPrice(pMax)}`;
    if (!isAtMin && isAtMax) return `Above ${sym}${compactPrice(pMin)}`;
    return `${sym}${compactPrice(pMin ?? 0)} – ${sym}${compactPrice(pMax)}`;
  })();

  const totalStyleCount = adventureStyles.length + broadCategories.length + (weekend ? 1 : 0);
  const adventureLabel = hasAdventure
    ? totalStyleCount === 1
      ? weekend
        ? "Weekend"
        : broadCategories.length === 1
          ? broadCategoryOptions.find((o) => o.value === broadCategories[0])?.label ||
            broadCategories[0]
          : adventureStyleLabelMap[adventureStyles[0]] || adventureStyles[0]
      : `${totalStyleCount} styles`
    : "Styles";

  const pillBase =
    "tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-sm tw-whitespace-nowrap tw-bg-white";

  return (
    <div
      className="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto tw-px-4 tw-py-2.5"
      style={{
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        backgroundColor: "transparent",
      }}
    >
      <button
        onClick={onOpenFilters}
        className={pillBase}
        style={{
          border: hasDuration ? "2px solid #222" : "1px solid #d1d5db",
          fontWeight: hasDuration ? 600 : 400,
          color: hasDuration ? "#222" : "#374151",
        }}
      >
        {durationLabel}
      </button>
      <button
        onClick={onOpenFilters}
        className={pillBase}
        style={{
          border: hasPrice ? "2px solid #222" : "1px solid #d1d5db",
          fontWeight: hasPrice ? 600 : 400,
          color: hasPrice ? "#222" : "#374151",
        }}
      >
        {priceLabel}
      </button>
      <button
        onClick={onOpenFilters}
        className={pillBase}
        style={{
          border: hasAdventure ? "2px solid #222" : "1px solid #d1d5db",
          fontWeight: hasAdventure ? 600 : 400,
          color: hasAdventure ? "#222" : "#374151",
        }}
      >
        {adventureLabel}
      </button>
    </div>
  );
};

export const Header = ({ homeAllTripsRef }) => {
  const { t } = useTranslation();
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const prevHref = useRef(window.location.href);

  const [hasQueryString, setHasQueryString] = useState(false);
  useEffect(() => {
    const search = window.location.search;
    if (
      search &&
      (search.includes("months") ||
        search.includes("destination") ||
        search.includes("search") ||
        search.includes("tab") ||
        search.includes("trip") ||
        search.includes("section") ||
        search.includes("region") ||
        search.includes("subregion") ||
        search.includes("duration_min") ||
        search.includes("duration_max") ||
        search.includes("price_min") ||
        search.includes("price_max") ||
        search.includes("adventure_styles") ||
        search.includes("broad_categories") ||
        search.includes("weekend"))
    ) {
      setHasQueryString(true);
    } else {
      setHasQueryString(false);
    }
  }, [window.location.search]);

  const { community } = useUserLocale();
  const [searchedTrips, setSearchedTrips] = useGlobalState("searchedTrips");

  const size = useWindowSize();
  const params = useQueryParams();
  let isInApp = window.isInApp;
  const isMobile = useIsMobile();

  const monthParam = params.get("months");
  const destinationParam = params.get("destination");
  const searchParam = params.get("search");
  const tabParam = params.get("tab");
  const sectionParam = params.get("section");
  const broadParam = params.get("broad");
  const regionParam = params.get("region");
  const subregionParam = params.get("subregion");
  const tripParam = params.get("trip");
  const projectParam = params.get("project");
  const weekParam = params.get("week");
  const yearParam = params.get("year");

  // Derive display title from section slug via i18n
  const sectionDisplayTitle =
    sectionParam && SECTION_TITLE_KEYS[sectionParam]
      ? t(SECTION_TITLE_KEYS[sectionParam])
      : sectionParam;

  // Helper to get date display text with week range if applicable
  const getDateDisplayText = (monthNum) => {
    if (weekParam && monthParam && !monthParam.includes(",")) {
      const weekNum = parseInt(weekParam, 10);
      const month = parseInt(monthParam, 10);
      const year = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();
      const daysInMonth = new Date(year, month, 0).getDate();

      let startDay, endDay;
      if (weekNum === 1) {
        startDay = 1;
        endDay = 7;
      } else if (weekNum === 2) {
        startDay = 8;
        endDay = 14;
      } else if (weekNum === 3) {
        startDay = 15;
        endDay = 21;
      } else if (weekNum === 4) {
        startDay = 22;
        endDay = 28;
      } else {
        startDay = 29;
        endDay = daysInMonth;
      }

      const monthName = translateMonth(monthNum);
      return `${startDay}-${endDay} ${monthName}`;
    }
    return translateMonth(monthNum);
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Sofest checkout theme: applied when reserve flow is launched from a
  // sofest landing page via `?special=sofest`. Drives dark bg + a second
  // sofest logo next to the Sotravel logo in the desktop header.
  const isSofestSpecial = new URLSearchParams(location.search).get("special") === "sofest";

  // Grouped browse views (region/subregion/month without other search params)
  // show drill-down rows instead of a flat list, so filters don't apply yet.
  // broad-only views (club, world, land, etc.) are excluded — they show filterable content.
  const isGroupedBrowse = !isEmbeddedTrips &&
    location.pathname === "/all-trips" &&
    ((regionParam &&
      !subregionParam &&
      !monthParam &&
      !destinationParam &&
      !searchParam &&
      !tripParam &&
      !sectionParam &&
      !projectParam) ||
      (subregionParam &&
        !monthParam &&
        !destinationParam &&
        !searchParam &&
        !tripParam &&
        !sectionParam &&
        !projectParam) ||
      (monthParam &&
        !weekParam &&
        !regionParam &&
        !subregionParam &&
        !destinationParam &&
        !searchParam &&
        !tripParam &&
        !sectionParam &&
        !projectParam &&
        !broadParam));

  // Trip filters (duration / price / styles) don't apply to grouped browse
  // rows or to the "Events near you" timeline.
  const hideTripFilters =
    isGroupedBrowse || (location.pathname === "/all-trips" && isEventsListView(params));

  const [conciergeOpen, setConciergeOpen] = useGlobalState("conciergeOpen");
  const isAuthenticated = localStorage.getItem("JWT_TOKEN") ? true : false;

  const [sortByHome, setSortByHome] = useGlobalState("sortByHome");
  const [showBroadCategories, setShowBroadCategories] = useGlobalState("showBroadCategories");
  const [user, setUser] = useGlobalState("user");

  const [innerWidth, setInnerWidth] = useState("71rem");
  const [navigateTo, setNavigateTo] = useState("/all-trips");

  const [partner, setPartner] = useState("singapore");

  const [headerFilterStyle, setHeaderFilterStyle] = useState({});
  const [iconFilterStyle, setIconFilterStyle] = useState({});
  const [iconTitleFilterStyle, setIconTitleFilterStyle] = useState({});
  const [iconBarCollapsed, setIconBarCollapsed] = useState(false);
  const [desktopScrolled, setDesktopScrolled] = useState(false);

  const headerFilterRef = useRef(null);

  // Desktop scroll tracking
  useEffect(() => {
    if (isMobile) return;
    // Reset on route change so icons reappear at scroll-top
    if (location.pathname === "/all-trips") {
      setDesktopScrolled(false);
    }
    if (!homeAllTripsRef?.current) return;
    const el = homeAllTripsRef.current;
    const onScroll = () => setDesktopScrolled(el.scrollTop > 10);
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [isMobile, homeAllTripsRef, location.pathname]);

  // Mobile scroll tracking
  useEffect(() => {
    if (!isMobile) return;

    // Reset on route change so icons reappear at scroll-top
    if (location.pathname === "/all-trips") {
      setIconBarCollapsed(false);
    }

    if (!homeAllTripsRef?.current) {
      return;
    }

    const scrollableElement = homeAllTripsRef.current;

    const handleScroll = () => {
      const scrollY = scrollableElement.scrollTop;

      // Icon bar collapse threshold for Group/Nearby/Concierge
      setIconBarCollapsed(scrollY > 12);

      const headerFilter = headerFilterRef.current;

      if (!headerFilter) {
        return;
      }

      const initialHeight = 70;
      const scrollThreshold = 29;
      if (scrollY <= scrollThreshold / 1.1) {
        const progress = scrollY / scrollThreshold / 2;
        const newHeight = initialHeight * (1 - progress);
        const newOpacity = 1 - progress * 1.3;

        setHeaderFilterStyle({
          height: `${newHeight}px`,
          overflow: "hidden",
        });

        setIconFilterStyle({
          opacity: newOpacity,
        });
        setIconTitleFilterStyle({
          marginBottom: "0px",
        });
      } else if (scrollY <= scrollThreshold) {
        const progress = scrollY / scrollThreshold / 2;
        const newHeight = initialHeight * (1 - progress);
        const newOpacity = 1 - progress;

        setHeaderFilterStyle({
          height: `${newHeight}px`,
          overflow: "hidden",
        });

        setIconFilterStyle({
          opacity: newOpacity,
        });

        setIconFilterStyle({
          opacity: 0,
          height: "17px",
        });
        setIconTitleFilterStyle({
          marginBottom: "14px",
        });
      } else {
        setHeaderFilterStyle({
          height: "2rem",
          overflow: "hidden",
        });
        setIconFilterStyle({
          opacity: 0,
          height: "14px",
        });
        setIconTitleFilterStyle({
          marginBottom: "14px",
        });
      }
    };

    // Add scroll listener to the actual scrollable element
    scrollableElement.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      scrollableElement.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile, homeAllTripsRef, location.pathname]);

  // Recover concierge panel state after login redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("recover_state") === "concierge") {
      setConciergeOpen(true);
      params.delete("recover_state");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [hideHeader] = useGlobalState("hideHeader");
  const [headerTranslucent] = useGlobalState("headerTranslucent");
  const showHeader =
    !hideHeader &&
    !(
    location.pathname === "/profile/duck" ||
    location.pathname === "/profile/duck/reviews" ||
    location.pathname.startsWith("/affiliate") ||
    location.pathname.startsWith("/request-affiliate") ||
    location.pathname.startsWith("/host/") ||
    /^\/club\/(?!\d+(?:\/|$))[^/]+\/?$/.test(location.pathname) ||
    location.pathname.startsWith("/club/manage") ||
    location.pathname === "/club/creation" ||
    location.pathname.startsWith("/signature/mongolia") ||
    location.pathname.startsWith("/signature/stans") ||
    location.pathname.startsWith("/group-leader-intro") ||
    location.pathname.startsWith("/our-leader-programme") ||
    location.pathname.startsWith("/our-leader-programme/apply") ||
    location.pathname.startsWith("/group-leader/apply") ||
    location.pathname === "/" ||
    location.pathname.startsWith("/leader-invitation") ||
    location.pathname.startsWith("/sofest") ||
    location.pathname.startsWith("/form/") ||
    countryWithLandingPage.includes(location.pathname.replace(/^\//, "")) ||
    location.pathname === "/ideas" ||
    location.pathname === "/faq" ||
    location.pathname === "/inferno" ||
    location.pathname === "/bring-your-guides" ||
    location.pathname === "/test-calendar" ||
    location.pathname === "/welcome-discount" ||
    location.pathname === "/claim-discount" ||
    location.pathname.startsWith("/c/") ||
    location.pathname === "/socials" ||
    location.pathname === "/community-page" ||
    location.pathname === "/about" ||
    location.pathname === "/404" ||
    location.pathname.toLowerCase().includes("project-s") ||
    location.pathname.toLowerCase().includes("login") ||
    location.pathname.toLowerCase().includes("privacy-policy") ||
    location.pathname.toLowerCase().includes("privacy-policy-app") ||
    location.pathname.toLowerCase().includes("terms-app") ||
    location.pathname.toLowerCase().includes("access") ||
    location.pathname.toLowerCase().includes("chat") ||
    (location.pathname.toLowerCase().includes("my-trips") &&
      location.pathname.toLowerCase().includes("action-items")) ||
    (location.pathname.toLowerCase().includes("my-trips") &&
      location.pathname.toLowerCase().includes("itinerary")) ||
    (location.pathname.toLowerCase().includes("my-trips") &&
      location.pathname.toLowerCase().includes("review")) ||
    (location.pathname.toLowerCase().includes("my-trips") &&
      location.pathname.toLowerCase().includes("upload")) ||
    location.pathname.startsWith("/checkin-preview") ||
    (location.pathname.toLowerCase().includes("my-trips") &&
      location.pathname.toLowerCase().includes("attendance")) ||
    location.pathname.startsWith("/sensei") ||
    (location.pathname.startsWith("/courses/") &&
      !location.pathname.endsWith("/edit") &&
      !location.pathname.includes("/enrollments"))
  );

  const [searchMobile, setSearchMobile] = useGlobalState("searchMobile");
  const handleOpenSearchMobile = () => {
    setSearchMobile(true);
  };
  const handleCloseSearchMobile = () => {
    setSearchMobile(false);
  };

  const getLocalPartner = async (url) => {
    let tripId;
    if (
      url === "book" ||
      url === "confirm" ||
      url === "booked" ||
      url === "refund" ||
      url === "reserve"
    ) {
      const query = queryString.parse(location.search);
      tripId = query.trip_id;
    } else if (url === "trips") {
      const params = location.pathname.split("/");
      tripId = params[params.length - 1];
    } else if (url === "trip") {
      const params = location.pathname.split("/");
      tripId = params[params.length - 1];
    }

    // Trip ID should be a number, else we don't fetch
    if (!isNumber(tripId)) {
      return;
    }
  };

  useEffect(() => {
    setShowBroadCategories(defaultBroadCategories(community));

    if (
      location.pathname === "/" ||
      location.pathname === "/mongolia" ||
      location.pathname === "/ideas"
    ) {
      setPartner("singapore");
      setNavigateTo("/");
    } else if (
      location.pathname === "/host" ||
      location.pathname === "/host/" ||
      location.pathname === "/my-trips" ||
      location.pathname === "/my-trips/" ||
      (location.pathname.includes("/all-trips") && location.search !== "")
    ) {
      setPartner("singapore");
      setNavigateTo("/all-trips");
      setInnerWidth("93vw");
    } else if (location.pathname === "/all-trips" || location.pathname === "/all-trips/") {
      setPartner("singapore");
      setNavigateTo("/");
      setInnerWidth("93vw");
    } else if (location.pathname.toLowerCase().includes("host")) {
      setNavigateTo("/host");
      getLocalPartner("trips");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else {
        setInnerWidth("72rem");
      }
    } else if (
      location.pathname === "/profile" ||
      location.pathname === "/profile/" ||
      location.pathname === "/notif-edit" ||
      location.pathname === "/notif-edit/"
    ) {
      setPartner("singapore");
      setNavigateTo("/all-trips");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else {
        setInnerWidth("76rem");
      }
    } else if (location.pathname === "/domore" || location.pathname === "/domore/") {
      setPartner("singapore");
      setNavigateTo("/all-trips");
      if (size.width < 1300) {
        setInnerWidth("90vw");
      } else {
        setInnerWidth("82vw");
      }
    } else if (
      location.pathname.toLowerCase().includes("trips") ||
      location.pathname.toLowerCase().includes("trip")
    ) {
      setNavigateTo("/all-trips");
      getLocalPartner("trips");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else if (size.width < 1600) {
        setInnerWidth("72rem");
      } else {
        setInnerWidth("81rem");
      }
    } else if (location.pathname.toLowerCase().includes("host")) {
      setNavigateTo("/host");
      getLocalPartner("trips");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else {
        setInnerWidth("72rem");
      }
    } else if (
      location.pathname.toLowerCase().includes("book") ||
      location.pathname.toLowerCase().includes("reserve")
    ) {
      setNavigateTo("/all-trips");
      getLocalPartner("book");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else {
        setInnerWidth("76rem");
      }
    } else if (
      location.pathname.toLowerCase().includes("confirm") ||
      location.pathname.toLowerCase().includes("booked") ||
      location.pathname.toLowerCase().includes("refund")
    ) {
      setNavigateTo("/all-trips");
      getLocalPartner("confirm");
      if (size.width < 1300) {
        setInnerWidth("95vw");
      } else {
        setInnerWidth("76rem");
      }
    }
  }, [size.width, location.pathname, location.search]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate("/profile");
  };

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  // const [selectedRegion, setSelectedRegion] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [allTripsFilterOpen, setAllTripsFilterOpen] = useState(false);
  const [filterFacetBounds, setFilterFacetBounds] = useState(null);

  const handleSelectDates = (date) => {
    if (selectedDates.includes(date)) {
      setSelectedDates(selectedDates.filter((selectedDate) => selectedDate !== date));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };
  // const handleSelectRegion = (region) => {
  //   if (selectedRegion === region) {
  //     setSelectedRegion("");
  //   } else {
  //     setSelectedRegion(region);
  //   }
  // };
  const handleSelectMonths = (month) => {
    if (selectedMonths.includes(month)) {
      setSelectedMonths(selectedMonths.filter((selectedMonth) => selectedMonth !== month));
    } else {
      setSelectedMonths([...selectedMonths, month]);
    }
  };

  // for users that click on filtered link
  const months = params.get("months");
  const destination = params.get("destination");

  useEffect(() => {
    if (months) {
      const monthsList = months.split(",").map((num) => parseInt(num.trim(), 10));

      setSelectedMonths(monthsList);

      const strMonths = [];
      for (const month of monthsList) {
        strMonths.push(dates[month - 1]);
      }

      setSelectedDates(strMonths);
    } else {
      setSelectedMonths([]);
      setSelectedDates([]);
    }

    // if (destination) {
    //   setSelectedRegion(destination);
    // }

    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [months, destination, searchParam]);

  const handleSearch = (isFilter = false) => {
    // setSortByHome("dates");

    let queryParams = new URLSearchParams();

    if (isFilter) {
      queryParams = new URLSearchParams(location.search);
      // if (countryWithLandingPage.includes(selectedRegion)) {
      //   navigate({
      //     pathname: `/${selectedRegion}`,
      //   });
      //   return;
      // }

      if (sortByHome !== "dates") {
        queryParams.set("broad", sortByHome);
      }

      if (selectedMonths.length > 0) {
        queryParams.set("months", selectedMonths);
        // we currently don't support filtering dates and tab at the same time
        const tab = queryParams.get("tab");
        if (tab) {
          queryParams.set("tab", "");
        }
      }

      // if (selectedRegion) {
      //   queryParams.set("destination", selectedRegion);
      // }
    } else {
      queryParams.set("search", searchQuery);
    }

    navigate({
      pathname: "/all-trips",
      search: queryParams.toString(),
    });

    window.scrollTo(0, 0);
  };

  const buildSearchQuery = (broad = "") => {
    const queryParams = new URLSearchParams();

    if (broad) {
      queryParams.set("broad", broad);
    }

    if (monthParam) {
      queryParams.set("months", monthParam);
    }

    if (destinationParam) {
      queryParams.set("destination", destinationParam);
    }

    if (searchParam) {
      queryParams.set("search", searchParam);
    }

    return queryParams.toString() ? `?${queryParams.toString()}` : "";
  };

  const firstCharToUpper = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleClearSelections = () => {
    // setSelectedRegion("");
    setSelectedDates([]);
    setSelectedMonths([]);
    setSearchQuery("");
  };

  const handleApplyAllTripsFilters = (filters) => {
    const nextParams = serializeAdvancedFilters(new URLSearchParams(location.search), filters);

    navigate({
      pathname: "/all-trips",
      search: nextParams.toString(),
    });
  };

  if (showHeader) {
    return (
      <div
        className={
          isSofestSpecial
            ? "headerWrapper sofest-special"
            : headerTranslucent
              ? `headerWrapper header-translucent${headerTranslucent === "clear" ? " is-clear" : ""}`
              : "headerWrapper"
        }
        style={
          conciergeOpen
            ? { height: "100vh", overflow: "hidden" }
            : location.pathname === "/all-trips" && isMobile
              ? { background: "linear-gradient(to bottom, #ffffff, #ffffff)" }
              : undefined
        }
      >
        <header
          style={
            location.pathname === "/all-trips" && isMobile
              ? {
                  padding: 0,
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  borderBottom: "none",
                }
              : !isMobile && location.pathname === "/all-trips"
                ? {
                    padding: 0,
                    boxShadow: "none",
                    backgroundColor: "transparent",
                    borderBottom: "none",
                  }
                : !isMobile
                  ? { borderBottom: "1px solid #f0f0f0", paddingBottom: "20px" }
                  : isMobile
                    ? { padding: 0 }
                    : {}
          }
        >
          {/* ─── DESKTOP HEADER ─── */}
          {!isMobile && location.pathname === "/all-trips" && (
            <div style={{ width: "100%" }}>
              {/* Row 1: Logo | Tabs or compact search | Menu */}
              <div
                className="tw-mx-auto tw-flex tw-items-center tw-justify-between tw-py-3"
                style={{ width: innerWidth }}
              >
                {/* Logo */}
                <button
                  onClick={() => navigate(navigateTo)}
                  className="tw-flex tw-shrink-0 tw-items-center"
                  style={{ width: "200px" }}
                >
                  <img
                    src="/new_logo/horizontal_blue.png"
                    alt="Sotravel"
                    style={{ height: "28px" }}
                  />
                </button>

                {/* Center: Icon tabs (expanded) or compact search pill (collapsed) */}
                <PopupState variant="popover" popupId="demo-popup-popover">
                  {(popupState) => (
                    <>
                      {!hasQueryString ? (
                        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center">
                          {/* Search pill — always visible, stays centered */}
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <div>
                              <button
                                className="tw-relative tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white hover:tw-shadow-lg"
                                style={{
                                  border: "1px solid #e0e0e0",
                                  boxShadow:
                                    "0 1px 6px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.04)",
                                  height: "48px",
                                  padding: "0 8px 0 20px",
                                  gap: "12px",
                                  minWidth: "280px",
                                  transition: "box-shadow 0.2s ease",
                                }}
                                {...bindTrigger(popupState)}
                              >
                                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center">
                                  <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-gray-900">
                                    {firstCharToUpper(
                                      sectionDisplayTitle ||
                                        tabParam ||
                                        broadParam ||
                                        searchParam ||
                                        (regionParam
                                          ? t(`region_${regionParam}`, {
                                              defaultValue: regionParam,
                                            })
                                          : null) ||
                                        (subregionParam
                                          ? t(`subregion_${subregionParam.replace(/-/g, "_")}`, {
                                              defaultValue: subregionParam,
                                            })
                                          : null) ||
                                        (destinationParam
                                          ? t(`destinations.${destinationParam?.toLowerCase()}`, {
                                              defaultValue: destinationParam,
                                            })
                                          : null) ||
                                        t("anywhere")
                                    )}
                                  </span>
                                  <span className="tw-text-xs tw-text-gray-500">
                                    {selectedDates.length > 0
                                      ? selectedDates.length === 1
                                        ? getDateDisplayText(selectedDates[0])
                                        : t("date_and_more", {
                                            date: getDateDisplayText(selectedDates[0]),
                                          })
                                      : t("any_month")}
                                  </span>
                                </div>
                                <SearchIcon
                                  style={{ marginLeft: "auto", flexShrink: 0 }}
                                  iconWidth="1.1rem"
                                  iconHeight="1.1rem"
                                />
                              </button>
                            </div>
                            {!hideTripFilters &&
                              (() => {
                                const af = parseAdvancedFilters(
                                  new URLSearchParams(location.search)
                                );
                                const activeCount =
                                  (af.durationMin != null || af.durationMax != null ? 1 : 0) +
                                  (af.priceMin != null || af.priceMax != null ? 1 : 0) +
                                  (af.adventureStyles.length > 0 ? 1 : 0);
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAllTripsFilterOpen(true);
                                    }}
                                    className="tw-relative tw-flex tw-h-10 tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-px-4 tw-transition-colors hover:tw-bg-gray-50"
                                    style={{
                                      border:
                                        activeCount > 0 ? "2px solid #222" : "1px solid #e0e0e0",
                                    }}
                                    aria-label="Open filters"
                                  >
                                    <TuneIcon style={{ fontSize: "18px", color: "#222" }} />
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                                      {t("filters_label", { defaultValue: "Filters" })}
                                    </span>
                                    {activeCount > 0 && (
                                      <span
                                        className="tw-absolute tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-900 tw-text-xs tw-font-bold tw-text-white"
                                        style={{ width: 20, height: 20, top: -6, right: -6 }}
                                      >
                                        {activeCount}
                                      </span>
                                    )}
                                  </button>
                                );
                              })()}
                          </div>
                        </div>
                      ) : (
                        /* hasQueryString — compact search + filters in center */
                        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-2">
                          <button
                            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-transition-shadow hover:tw-shadow-lg"
                            style={{
                              border: "1px solid #e0e0e0",
                              boxShadow: "0 1px 6px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.04)",
                              height: "48px",
                              padding: "0 8px 0 20px",
                              gap: "12px",
                              minWidth: "280px",
                            }}
                            {...bindTrigger(popupState)}
                          >
                            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center">
                              <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-gray-900">
                                {firstCharToUpper(
                                  sectionDisplayTitle ||
                                    tabParam ||
                                    broadParam ||
                                    searchParam ||
                                    (regionParam
                                      ? t(`region_${regionParam}`, { defaultValue: regionParam })
                                      : null) ||
                                    (subregionParam
                                      ? t(`subregion_${subregionParam.replace(/-/g, "_")}`, {
                                          defaultValue: subregionParam,
                                        })
                                      : null) ||
                                    (destinationParam
                                      ? t(`destinations.${destinationParam?.toLowerCase()}`, {
                                          defaultValue: destinationParam,
                                        })
                                      : null) ||
                                    t("anywhere")
                                )}
                              </span>
                              <span className="tw-text-xs tw-text-gray-500">
                                {selectedDates.length > 0
                                  ? selectedDates.length === 1
                                    ? getDateDisplayText(selectedDates[0])
                                    : t("date_and_more", {
                                        date: getDateDisplayText(selectedDates[0]),
                                      })
                                  : t("any_month")}
                              </span>
                            </div>
                            <SearchIcon
                              style={{ marginLeft: "auto", flexShrink: 0 }}
                              iconWidth="1.1rem"
                              iconHeight="1.1rem"
                            />
                          </button>
                          {!hideTripFilters &&
                            (() => {
                              const af = parseAdvancedFilters(new URLSearchParams(location.search));
                              const activeCount =
                                (af.durationMin != null || af.durationMax != null ? 1 : 0) +
                                (af.priceMin != null || af.priceMax != null ? 1 : 0) +
                                (af.adventureStyles.length > 0 ? 1 : 0);
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAllTripsFilterOpen(true);
                                  }}
                                  className="tw-relative tw-flex tw-h-10 tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-px-4 tw-transition-colors hover:tw-bg-gray-50"
                                  style={{
                                    border:
                                      activeCount > 0 ? "2px solid #222" : "1px solid #e0e0e0",
                                  }}
                                  aria-label="Open filters"
                                >
                                  <TuneIcon style={{ fontSize: "18px", color: "#222" }} />
                                  <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                                    {t("filters_label", { defaultValue: "Filters" })}
                                  </span>
                                  {activeCount > 0 && (
                                    <span
                                      className="tw-absolute tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-900 tw-text-xs tw-font-bold tw-text-white"
                                      style={{ width: 20, height: 20, top: -6, right: -6 }}
                                    >
                                      {activeCount}
                                    </span>
                                  )}
                                </button>
                              );
                            })()}
                        </div>
                      )}
                      <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                        PaperProps={{
                          sx: {
                            borderRadius: "30px",
                            marginTop: "10px",
                            width: "53rem",
                            maxHeight: "39.125rem",
                          },
                          id: "popoverContainer",
                        }}
                      >
                        <Filter
                          selectedDates={selectedDates}
                          handleSelectDates={handleSelectDates}
                          closePopUp={() => popupState.close()}
                          handleSelectMonths={handleSelectMonths}
                          handleSearch={handleSearch}
                          handleClearSelections={handleClearSelections}
                          dates={dates}
                          searchQuery={searchQuery}
                          onChangeSearch={setSearchQuery}
                        />
                      </Popover>
                    </>
                  )}
                </PopupState>

                {/* Right: Language + Menu */}
                <div
                  className="tw-flex tw-shrink-0 tw-items-center tw-gap-1"
                  style={{ width: "200px", justifyContent: "flex-end" }}
                >
                  <LanguageSelector />
                  <MenuButton
                    handleClick={handleClick}
                    handleClose={handleClose}
                    anchorEl={anchorEl}
                    handleProfileClick={handleProfileClick}
                    open={open}
                    dark={isSofestSpecial}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ─── DESKTOP: Non all-trips pages ─── */}
          {!isMobile && location.pathname !== "/all-trips" && (
            <div
              className="header-inner"
              style={{ width: innerWidth, justifyContent: "space-between" }}
            >
              <div
                style={{
                  width: "30%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "4px",
                }}
              >
                {!isSofestSpecial && (
                  <button
                    onClick={() => navigate(navigateTo)}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div className="tw-grow md:tw-grow-0">
                      <img
                        src="/new_logo/horizontal_blue.png"
                        style={{ height: "30px", margin: "8px" }}
                        alt="Sotravel"
                      />
                    </div>
                  </button>
                )}
                {isSofestSpecial && (
                  <a
                    href="/sofest"
                    aria-label="SoFest landing page"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {/* Uses the same CloudFront-hosted transparent PNG as
                        src/pages/sofest/components/Navbar.js. The local
                        /images/sofest-logo.png ships with a white
                        background and shows as a white box against the
                        dark sofest header. */}
                    <img
                      src="https://d1puf2nnbcyl9q.cloudfront.net/1778216586768_sofest-logo.png.png"
                      alt="SoFest"
                      style={{ height: "30px", margin: "8px" }}
                    />
                  </a>
                )}
              </div>
              {location.pathname === "/all-trips" && (
                <PopupState variant="popover" popupId="demo-popup-popover">
                  {(popupState) => (
                    <>
                      <div
                        style={{
                          width: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "8px",
                          paddingTop: "5px",
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                        }}
                        id="header-search-red-container"
                      >
                        <button className="header-search-container" {...bindTrigger(popupState)}>
                          <div className="header-search-detail-wrapper">
                            <p>{t("anywhere")}</p>
                            <span>{t("any_month")}</span>
                          </div>
                          <SearchIcon
                            style={{ position: "absolute", right: "6px" }}
                            iconWidth="1.2rem"
                            iconHeight="1.2rem"
                          />
                        </button>
                      </div>
                      <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                        PaperProps={{
                          sx: {
                            borderRadius: "30px",
                            marginTop: "10px",
                            width: "53rem",
                            maxHeight: "39.125rem",
                          },
                          id: "popoverContainer",
                        }}
                      >
                        <Filter
                          selectedDates={selectedDates}
                          handleSelectDates={handleSelectDates}
                          closePopUp={() => popupState.close()}
                          handleSelectMonths={handleSelectMonths}
                          handleSearch={handleSearch}
                          handleClearSelections={handleClearSelections}
                          dates={dates}
                          searchQuery={searchQuery}
                          onChangeSearch={setSearchQuery}
                        />
                      </Popover>
                    </>
                  )}
                </PopupState>
              )}
              <div className="tw-flex tw-items-center tw-gap-2">
                <LanguageSelector />
                <MenuButton
                  handleClick={handleClick}
                  handleClose={handleClose}
                  anchorEl={anchorEl}
                  handleProfileClick={handleProfileClick}
                  open={open}
                  dark={isSofestSpecial}
                />
              </div>
            </div>
          )}

          {/* ─── MOBILE HEADER ─── */}
          <div
            className="header-inner"
            style={isMobile ? { width: innerWidth, justifyContent: "center" } : { display: "none" }}
          >
            {location.pathname === "/all-trips" && isMobile && !conciergeOpen && (
              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <>
                    <div
                      className="header-mobile-row"
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 16px",
                        boxSizing: "border-box",
                      }}
                    >
                      {(isEmbeddedTrips || hasQueryString || (broadParam && broadParam !== "club")) && (
                        <button
                          onClick={() => {
                            if (isEmbeddedTrips) { notifyTripsParent("close"); return; }
                            const before = prevHref.current;
                            if (before === window.location.href) {
                              navigate("/all-trips");
                            } else {
                              navigate(-1);
                            }
                          }}
                          className="tw-flex tw-shrink-0 tw-items-center tw-justify-center"
                          style={{
                            width: 36,
                            height: 36,
                            border: "none",
                            background: "none",
                            padding: 0,
                          }}
                        >
                          <ArrowBackIcon style={{ color: "#222", fontSize: "1.25rem" }} />
                        </button>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button
                          onClick={handleOpenSearchMobile}
                          style={{
                            width: "100%",
                            height: 48,
                            borderRadius: 24,
                            backgroundColor: "white",
                            border: "none",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 20px",
                            cursor: "pointer",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#222",
                              lineHeight: 1.3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {firstCharToUpper(
                              sectionDisplayTitle ||
                                tabParam ||
                                broadParam ||
                                searchParam ||
                                (regionParam
                                  ? t(`region_${regionParam}`, { defaultValue: regionParam })
                                  : null) ||
                                (subregionParam
                                  ? t(`subregion_${subregionParam.replace(/-/g, "_")}`, {
                                      defaultValue: subregionParam,
                                    })
                                  : null) ||
                                (destinationParam
                                  ? t(`destinations.${destinationParam.toLowerCase()}`, {
                                      defaultValue: destinationParam,
                                    })
                                  : null) ||
                                t("anywhere")
                            )}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: "#717171",
                              lineHeight: 1.3,
                            }}
                          >
                            {selectedDates.length > 0
                              ? selectedDates.length === 1
                                ? getDateDisplayText(selectedDates[0])
                                : t("date_and_more", { date: getDateDisplayText(selectedDates[0]) })
                              : t("any_month")}
                          </span>
                        </button>
                      </div>

                      {hasQueryString ? (
                        <CopyToClipboard
                          text={publicTripsUrl(location.pathname, location.search)}
                        >
                          <button
                            onClick={() => {
                              setOpenAlertBannerMsg(t("page_link_copied"));
                            }}
                            className="tw-flex tw-shrink-0 tw-items-center tw-justify-center"
                            style={{
                              width: 36,
                              height: 36,
                              border: "none",
                              background: "none",
                              padding: 0,
                            }}
                          >
                            <IosShareOutlinedIcon style={{ fontSize: "20px", color: "#222" }} />
                          </button>
                        </CopyToClipboard>
                      ) : (
                        <button
                          onClick={() => setAllTripsFilterOpen(true)}
                          className="tw-flex tw-shrink-0 tw-items-center tw-rounded-full tw-bg-white"
                          style={{ height: 44, border: "1px solid #e0e0e0" }}
                          aria-label="Open filters"
                        >
                          <div
                            className="tw-flex tw-items-center tw-justify-center tw-px-2.5"
                            style={{ height: "100%" }}
                          >
                            <TuneIcon style={{ fontSize: "20px", color: "#111827" }} />
                          </div>
                          {!isInApp && (
                            <div
                              className="tw-flex tw-items-center tw-justify-center tw-pr-1"
                              style={{ height: "100%" }}
                            >
                              <div
                                style={{
                                  height: "2.2rem",
                                  width: "2.2rem",
                                  backgroundColor: user && user.image ? "lightgrey" : "#f3f4f6",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                }}
                              >
                                {user && user.image ? (
                                  <ImageResizable
                                    style={{
                                      height: "100%",
                                      width: "100%",
                                      objectFit: "cover",
                                      borderRadius: "50%",
                                    }}
                                    alt={user.first_name}
                                    src={user.image}
                                    size="s"
                                  />
                                ) : (
                                  <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full">
                                    <PersonOutlinedIcon
                                      style={{ fontSize: 22, color: "#6b7280" }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </button>
                      )}

                      <FilterMobile
                        selectedDates={selectedDates}
                        handleSelectDates={handleSelectDates}
                        searchMobile={searchMobile}
                        handleCloseSearchMobile={handleCloseSearchMobile}
                        handleSelectMonths={handleSelectMonths}
                        handleSearch={handleSearch}
                        handleClearSelections={handleClearSelections}
                        dates={dates}
                        search={searchQuery}
                        onChangeSearch={setSearchQuery}
                      />
                    </div>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                      transformOrigin={{ vertical: "top", horizontal: "center" }}
                      PaperProps={{
                        sx: {
                          borderRadius: "30px",
                          marginTop: "10px",
                          width: "53rem",
                          maxHeight: "39.125rem",
                        },
                        id: "popoverContainer",
                      }}
                    >
                      <Filter
                        selectedDates={selectedDates}
                        handleSelectDates={handleSelectDates}
                        closePopUp={() => popupState.close()}
                        handleSelectMonths={handleSelectMonths}
                        handleSearch={handleSearch}
                        handleClearSelections={handleClearSelections}
                        dates={dates}
                        searchQuery={searchQuery}
                        onChangeSearch={setSearchQuery}
                      />
                    </Popover>
                  </>
                )}
              </PopupState>
            )}
          </div>
        </header>

        {/* ─── DESKTOP ICON BAR (Group / Nearby / Concierge) ─── */}
        {location.pathname === "/all-trips" && !hasQueryString && !isMobile && (
          <AnimatePresence>
            {!desktopScrolled && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 30, mass: 0.8 }}
                style={{ overflow: "hidden", background: "#fff" }}
              >
                <div className="tw-flex tw-items-end tw-justify-center tw-gap-10 tw-py-2">
                  {[
                    {
                      key: "group",
                      iconSrc: "/icons/Group.svg",
                      labelKey: "icon_bar_group",
                      defaultLabel: "Group",
                      active: !conciergeOpen && broadParam !== "club",
                      onClick: () => {
                        setConciergeOpen(false);
                        navigate("/all-trips");
                        window.scrollTo(0, 0);
                      },
                    },
                    {
                      key: "nearby",
                      iconSrc: "/icons/Nearby.svg",
                      labelKey: "icon_bar_nearby",
                      defaultLabel: "Nearby",
                      active: !conciergeOpen && broadParam === "club",
                      onClick: () => {
                        if (window.nearbyEnabled && window.ReactNativeWebView) {
                          window.ReactNativeWebView.postMessage(
                            JSON.stringify({ type: "NAVIGATE_TO_NEARBY" })
                          );
                          return;
                        }
                        setConciergeOpen(false);
                        navigate("/all-trips?broad=club");
                        window.scrollTo(0, 0);
                      },
                    },
                    {
                      key: "concierge",
                      iconSrc: "/icons/Concierge.svg",
                      labelKey: "icon_bar_concierge",
                      defaultLabel: "Concierge",
                      active: conciergeOpen,
                      onClick: () => {
                        // if (!isAuthenticated) {
                        //   navigate(
                        //     `/login?reroute=${encodeURIComponent("/all-trips?recover_state=concierge")}`
                        //   );
                        // } else {
                        //   setConciergeOpen(true);
                        // }
                        console.log("coming soon");
                      },
                    },
                  ].map(({ key, iconSrc, labelKey, defaultLabel, active, onClick }) => (
                    <button
                      key={key}
                      onClick={onClick}
                      className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-pb-2 tw-transition-colors"
                      style={{
                        cursor: "pointer",
                        opacity: active ? 1 : 0.45,
                        minWidth: "72px",
                        background: "none",
                        border: "none",
                        borderBottom: active ? "2px solid #222" : "2px solid transparent",
                      }}
                    >
                      <img
                        src={iconSrc}
                        alt={defaultLabel}
                        width={40}
                        height={40}
                        style={{ objectFit: "contain" }}
                      />
                      <span
                        className="tw-text-xs"
                        style={{
                          color: active ? "#222" : "#717171",
                          letterSpacing: "0.01em",
                          fontWeight: 600,
                        }}
                      >
                        {t(labelKey, { defaultValue: defaultLabel })}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* ─── MOBILE ICON BAR (Group / Nearby / Concierge) ─── */}
        {location.pathname === "/all-trips" && !hasQueryString && isMobile && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              gap: "36px",
              padding: iconBarCollapsed ? "6px 0 8px" : "5px 0 12px",
              background: "#fff",
            }}
          >
            {[
              {
                key: "group",
                iconSrc: "/icons/Group.svg",
                labelKey: "icon_bar_group",
                defaultLabel: "Group",
                active: !conciergeOpen && broadParam !== "club",
                onClick: () => {
                  setConciergeOpen(false);
                  navigate("/all-trips");
                  window.scrollTo(0, 0);
                },
              },
              {
                key: "nearby",
                iconSrc: "/icons/Nearby.svg",
                labelKey: "icon_bar_nearby",
                defaultLabel: "Nearby",
                active: !conciergeOpen && broadParam === "club",
                onClick: () => {
                  if (window.nearbyEnabled && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                      JSON.stringify({ type: "NAVIGATE_TO_NEARBY" })
                    );
                    return;
                  }
                  setConciergeOpen(false);
                  navigate("/all-trips?broad=club");
                  window.scrollTo(0, 0);
                },
              },
              {
                key: "concierge",
                iconSrc: "/icons/Concierge.svg",
                labelKey: "icon_bar_concierge",
                defaultLabel: "Concierge",
                active: conciergeOpen,
                onClick: () => {
                  // if (!isAuthenticated) {
                  //   navigate(
                  //     `/login?reroute=${encodeURIComponent("/all-trips?recover_state=concierge")}`
                  //   );
                  // } else {
                  //   setConciergeOpen(true);
                  // }
                  console.log("coming soon");
                },
              },
            ].map(({ key, iconSrc, labelKey, defaultLabel, active, onClick }) => (
              <div
                key={key}
                onClick={onClick}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  opacity: active ? 1 : 0.4,
                  minWidth: "56px",
                }}
              >
                <motion.div
                  initial={{ height: 24, opacity: 1, marginBottom: 4 }}
                  animate={{
                    height: iconBarCollapsed ? 0 : 33,
                    opacity: iconBarCollapsed ? 0 : 1,
                    marginBottom: iconBarCollapsed ? 0 : 4,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 28,
                    mass: 0.8,
                  }}
                  style={{
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={iconSrc}
                    alt={defaultLabel}
                    width={33}
                    height={33}
                    style={{ objectFit: "contain" }}
                  />
                </motion.div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: active ? "#222" : "#999",
                    letterSpacing: "0.01em",
                    whiteSpace: "nowrap",
                    paddingBottom: active ? "4px" : "5px",
                    borderBottom: active ? "2px solid #222" : "none",
                  }}
                >
                  {t(labelKey, { defaultValue: defaultLabel })}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ─── CONCIERGE CHAT PANEL ─── */}
        {conciergeOpen && location.pathname === "/all-trips" && !hasQueryString && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.96, y: -10 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 130, damping: 22, mass: 1.4 }}
            style={{
              flex: 1,
              overflow: "hidden",
              minHeight: 0,
              transformOrigin: "top",
              width: isMobile ? "98%" : "57%",
            }}
          >
            <ConciergeChatPanel />
          </motion.div>
        )}

        {location.pathname === "/all-trips" && (
          <AllTripsFilterSheet
            open={allTripsFilterOpen}
            onClose={() => setAllTripsFilterOpen(false)}
            locationSearch={location.search}
            isInApp={isInApp}
            user={user}
            onApplyFilters={handleApplyAllTripsFilters}
            onFacetBoundsChange={setFilterFacetBounds}
            baseQueryParams={{
              months: monthParam,
              destination: destinationParam,
              search: searchParam,
              trip: tripParam,
              section: sectionParam,
              userId: user?.id,
              region: regionParam,
              project: projectParam,
              subregion: subregionParam,
              week: weekParam,
              year: yearParam,
              broad: broadParam,
            }}
          />
        )}

        {location.pathname === "/all-trips" && hasQueryString && isMobile && !hideTripFilters && (
          <FilterPillsRow
            locationSearch={location.search}
            onOpenFilters={() => setAllTripsFilterOpen(true)}
            facetBounds={filterFacetBounds}
          />
        )}

        {/* Category bar hidden — replaced by "Explore by adventure style" tiles in Home.js */}
        {false && location.pathname === "/all-trips" && !hasQueryString && (
          <div
            className="headerFilter"
            ref={headerFilterRef}
            style={{
              ...headerFilterStyle,
              ...(isMobile
                ? { backgroundColor: "transparent", boxShadow: "none", borderBottom: "none" }
                : {}),
            }}
          >
            <div className="headerFilterContainer" style={{ width: innerWidth }}>
              <button
                className="headerFilterChoiceContainer"
                style={sortByHome === "dates" ? { color: "black" } : { color: "grey" }}
                onClick={() => {
                  setSortByHome("dates");

                  const search = buildSearchQuery();

                  navigate({
                    pathname: "/all-trips",
                    search: search,
                  });
                  window.scrollTo(0, 0);
                }}
              >
                <CalendarMonthOutlinedIcon style={iconFilterStyle} />
                <span style={iconTitleFilterStyle}>{t("new")}</span>
              </button>

              <button
                className="headerFilterChoiceContainer"
                style={
                  sortByHome === "world"
                    ? { color: "black", height: "2.7rem", marginBottom: "3px" }
                    : { color: "grey", height: "2.7rem", marginBottom: "3px" }
                }
                onClick={() => {
                  setSortByHome("world");
                  window.scrollTo(0, 0);

                  const search = buildSearchQuery("world");

                  navigate({
                    pathname: "/all-trips",
                    search: search,
                  });
                }}
              >
                <GradeOutlinedIcon style={{ ...iconFilterStyle, fontSize: "20px" }} />
                <span
                  style={{
                    ...iconTitleFilterStyle,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    fontSize: "11px",
                    lineHeight: "0.8",
                  }}
                >
                  {t("world_wonder_category")
                    .split(" ")
                    .map((word, index) => (
                      <React.Fragment key={index}>
                        {word}
                        <br style={{ lineHeight: "0.8" }} />
                      </React.Fragment>
                    ))}
                </span>
              </button>

              {showBroadCategories["360"] && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "360" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("360");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("360");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <PublicIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("360", {
                      defaultValue: "360°",
                    })}
                  </span>
                </button>
              )}

              {showBroadCategories.land && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "land" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("land");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("land");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <ForestOutlinedIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("hike", {
                      defaultValue: "Hike",
                    })}
                  </span>
                </button>
              )}

              {showBroadCategories.snow && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "snow" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("snow");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("snow");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <AcUnitOutlinedIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("snow", {
                      defaultValue: "Snow",
                    })}
                  </span>
                </button>
              )}

              {showBroadCategories.sea && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "sea" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("sea");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("sea");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <WaterOutlinedIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>{t("sea")}</span>
                </button>
              )}

              {showBroadCategories.surf && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "surf" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("surf");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("surf");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <WaterOutlinedIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("surf", {
                      defaultValue: "Surf",
                    })}
                  </span>
                </button>
              )}

              {showBroadCategories.dive && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "dive" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("dive");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("dive");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <PropaneTankOutlinedIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("dive", {
                      defaultValue: "Dive",
                    })}
                  </span>
                </button>
              )}

              {showBroadCategories.club && (
                <button
                  className="headerFilterChoiceContainer"
                  style={sortByHome === "club" ? { color: "black" } : { color: "grey" }}
                  onClick={() => {
                    setSortByHome("club");
                    window.scrollTo(0, 0);

                    const search = buildSearchQuery("club");

                    navigate({
                      pathname: "/all-trips",
                      search: search,
                    });
                  }}
                >
                  <FavoriteBorderIcon style={iconFilterStyle} />
                  <span style={iconTitleFilterStyle}>
                    {t("club", {
                      defaultValue: "Club",
                    })}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
};

export function SearchIcon({ style, iconWidth, iconHeight }) {
  return (
    <motion.div
      style={{
        width: "2rem",
        height: "2rem",
        background: "deepskyblue",
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...style,
      }}
    >
      <SearchOutlinedIcon
        style={{
          color: "white",
          width: iconWidth,
          height: iconHeight,
        }}
      />
    </motion.div>
  );
}

function LanguageSelector() {
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const { t } = useTranslation();

  const [user] = useGlobalState("user");

  return (
    <motion.div className="tw-relative">
      {user.is_admin && (
        <button
          onClick={() => setLanguageModalOpen(true)}
          className="tw-rounded-full tw-p-2 tw-transition-colors hover:tw-bg-gray-100"
          aria-label={t("preferences.change_language", "Change language")}
        >
          <Globe className="tw-h-5 tw-w-5" />
        </button>
      )}
      <LanguagePortal
        languageModalOpen={languageModalOpen}
        onHide={() => setLanguageModalOpen(false)}
      />
    </motion.div>
  );
}

function getCommunityDefaultLang(community) {
  if (community === "sg") return "en";

  if (community === "my") return "zh-Hans-CN";

  if (community === "tw") return "zh-Hant-TW";

  return "en";
}

export function LanguagePortal({ languageModalOpen, onHide }) {
  const { community: globalCommunity, handleChangeLocale } = useUserLocale();

  const { i18n, t } = useTranslation();
  const [activeTab, setActiveTab] = useState("language");
  const [user] = useGlobalState("user");

  const [community, setCommunity] = useState(globalCommunity);

  useEffect(() => {
    setCommunity(globalCommunity);
  }, [globalCommunity, languageModalOpen]);

  const { isAuthenticated } = useIsAuthenticated();

  const getDefaultPreferredLanguage = useCallback(() => {
    if (user?.preferred_language) {
      return user.preferred_language;
    }
    return i18n.language;
  }, [i18n.language, user?.preferred_language]);

  const [preferredLanguage, setPreferredLanguage] = useState(getDefaultPreferredLanguage());

  const [isSaving, setIsSaving] = useState(false);

  const resetState = useCallback(() => {
    setPreferredLanguage(getDefaultPreferredLanguage());
    setCommunity(globalCommunity);
    setActiveTab("language");
  }, [getDefaultPreferredLanguage, globalCommunity]);

  // Reset states when user changes or language changes
  useEffect(() => {
    resetState();
  }, [languageModalOpen, resetState]);

  const handleCloseCommunitySettings = useCallback(() => {
    resetState();
    onHide();
  }, [resetState, onHide]);

  const handleSaveCommunitySettings = useCallback(async () => {
    // Validate if all required fields are selected
    if (!preferredLanguage || !community) {
      return; // Don't proceed if required fields are missing
    }

    setIsSaving(true);

    // Store language change info but don't apply it yet
    const hasChangedPreferred =
      (preferredLanguage && preferredLanguage !== i18n.language) || community !== globalCommunity;

    try {
      if (isAuthenticated) {
        // Update user preferences in backend
        await authAxios.post("/v1/user/updateUserPreferredLanguages", {
          preferred_language: preferredLanguage,
          community: community,
        });
      }

      // Only after API call is complete, apply language change if needed
      if (hasChangedPreferred) {
        localStorage.setItem("locale", preferredLanguage);
        localStorage.setItem("community", community);
        localStorage.setItem("currency", communityToCurrencyMap[community]);

        if (user?.is_admin && community !== globalCommunity) {
          setAdminNearbyLocationForCommunity(community);
        }

        if (window.isInApp) {
          window.postMessage(`locale:${preferredLanguage}`);
          window.postMessage(`community:${community}`);
          window.postMessage(`currency:${communityToCurrencyMap[community]}`);
          onHide();
        } else {
          onHide();
          window.location.reload();
        }
      }

      onHide();
    } catch (error) {
      console.error("Error updating user preferred languages:", error);
    } finally {
      setIsSaving(false);
    }
  }, [preferredLanguage, community, i18n.language, isAuthenticated, onHide, globalCommunity, user?.is_admin]);

  const handleCommunityChange = (community) => {
    const defaultLang = getCommunityDefaultLang(community);
    setPreferredLanguage(defaultLang);
    setCommunity(community);

    setActiveTab("language");
  };

  const handleLanguageSelect = (lang) => {
    setPreferredLanguage(lang);
  };

  const disabledSave =
    isSaving ||
    !preferredLanguage ||
    !community ||
    (community === globalCommunity && preferredLanguage === i18n.language);

  // Kit dialog (pages/manage/ui.js), like the account menu it opens from.
  const languageOptions = [
    (community === "sg" || community === "my" || community === "tw" || community === "id") && {
      value: "en",
      icon: <Globe className="tw-size-[18px] tw-text-muted-foreground" />,
      label: t("preferences.english", "English"),
      hint: t("preferences.english_desc", "Experience our platform in English"),
    },
    community === "my" && {
      value: "zh-Hans-CN",
      icon: <span className="tw-text-lg tw-leading-none">🇲🇾</span>,
      label: t("preferences.chinese", "简体中文"),
      hint: t("preferences.chinese_desc", "Experience our platform in Simplified Chinese"),
    },
    community === "tw" && {
      value: "zh-Hant-TW",
      icon: <span className="tw-text-lg tw-leading-none">🇹🇼</span>,
      label: t("preferences.traditional_chinese", "繁體中文"),
      hint: t("preferences.traditional_chinese_desc", "Experience our platform in Traditional Chinese"),
    },
  ].filter(Boolean);

  const communityOptions = [
    { value: "sg", flag: "🇸🇬", label: t("preferences.singapore", "Singapore"), hint: t("preferences.singapore_desc", "Travel with Singaporeans") },
    { value: "my", flag: "🇲🇾", label: t("preferences.malaysia", "Malaysia"), hint: t("preferences.malaysia_desc", "Travel with Malaysians") },
    { value: "tw", flag: "🇹🇼", label: t("preferences.taiwan", "Taiwan"), hint: t("preferences.taiwan_desc", "Travel with Taiwanese") },
    { value: "id", flag: "🇮🇩", label: t("preferences.indonesia", "Indonesia"), hint: t("preferences.indonesia_desc", "Travel with Indonesians") },
  ].map((c) => ({ ...c, icon: <span className="tw-text-lg tw-leading-none">{c.flag}</span> }));

  const optionList = (options, selected, onSelect) => (
    <div role="radiogroup" className="tw-overflow-hidden tw-rounded-[var(--r-card)] tw-bg-[var(--fill)] [&>*+*]:tw-border-t [&>*+*]:tw-border-[var(--hairline)]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={selected === o.value}
          onClick={() => onSelect(o.value)}
          className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-text-left tw-transition-colors hover:tw-bg-[var(--fill)]"
        >
          <span className="tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center">{o.icon}</span>
          <span className="tw-min-w-0 tw-flex-1">
            <span className="tw-block tw-text-[15px] tw-font-medium">{o.label}</span>
            <span className="tw-block tw-text-[13px] tw-text-muted-foreground">{o.hint}</span>
          </span>
          <span
            className={`tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-transition-colors ${
              selected === o.value ? "tw-border-[hsl(var(--brand))]" : "tw-border-[var(--fill-stronger)]"
            }`}
          >
            {selected === o.value && <span className="tw-size-2.5 tw-rounded-full tw-bg-[hsl(var(--brand))]" />}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <KitDialog
      open={languageModalOpen}
      onClose={handleCloseCommunitySettings}
      title={user.is_admin ? t("preferences.title", "Select community and language") : t("preferences.language", "Language")}
      footer={
        <>
          <KitButton variant="light" onClick={handleCloseCommunitySettings} disabled={isSaving}>
            {t("cancel", { defaultValue: "Cancel" })}
          </KitButton>
          <KitButton onClick={handleSaveCommunitySettings} disabled={disabledSave} loading={isSaving}>
            {isSaving ? t("saving", "Saving...") : t("save", { defaultValue: "Save" })}
          </KitButton>
        </>
      }
    >
      {user.is_admin && (
        <KitSegmented
          className="tw-mb-4"
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: "community", label: t("preferences.community", "Community") },
            { value: "language", label: t("preferences.language", "Language") },
          ]}
        />
      )}
      {activeTab === "language" ? optionList(languageOptions, preferredLanguage, handleLanguageSelect) : optionList(communityOptions, community, handleCommunityChange)}
    </KitDialog>
  );
}
